import React, { useEffect, useState } from 'react'
import { TextInput, useTranslate, useNotify, SelectInput, FormDataConsumer, required, useGetIdentity } from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import { Grid, Skeleton, CircularProgress } from '@mui/material'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { documentTypeServices } from 'src/services/reservation/documentType/documentType.service'
import { typeResponsibleService } from 'src/services/reservation/typeResponsible/typeResponsible.service'
import { businessUnitServices } from 'src/services/reservation/businessUnit/businessUnit.service'
import {
  CONSUMIDOR_FINAL_NAME,
  MONOTRIBUTISTA,
  RESPONSABLE_MONOTRIBUTISTA,
  RESPONSABLE_INSCRIPTO,
  EMPRESA,
} from 'src/components/pages/administration/business-unit/logic/contrant'
import { clientServices } from 'src/services/auth/client/client.service'


const BusinessName = () => {
  const translate = useTranslate()
  const { setValue } = useFormContext()
  const short_name = useWatch({ name: 'short_name' })
  const long_name = useWatch({ name: 'long_name' })

  useEffect(() => {
    setValue('fiscal_data_relation.name', '')
    if (short_name && long_name) {
      setValue('fiscal_data_relation.name', `${short_name} ${long_name}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [short_name, long_name])

  return (
    <EditInputsWrap label={translate('ra.field.business_name')}>
      <TextInput
        source='fiscal_data_relation.name'
        label=''
        fullWidth
        disabled
        helperText='Se completara con su nombre y apellido'
      />
    </EditInputsWrap>
  )
}

const DocumentNumberInput = ({ record }) => {
  const translate = useTranslate()
  const documentNumber = useWatch({ name: 'fiscal_data_relation.document_number' })
  const documentType = useWatch({ name: 'fiscal_data_relation.document_type_id' })
  const [cuitError, setCuitError] = useState('')
  const [RUTError, setCuitRUTError] = useState('')
  const [isValidating, setIsValidating] = useState(false)
  const [existsError, setExistsError] = useState('')
  const [loadingExistDocumentNumber, setLoadingExistDocumentNumber] = useState(false)

  useEffect(() => {
    if (documentType === 2) {
      // Assuming "2" is the ID for CUIT
      setIsValidating(true)
      const timeout = setTimeout(() => {
        if (documentNumber && !validateCUIT(documentNumber)) {
          setCuitError(translate('ra.message.error_CUIT'))
        } else {
          setCuitError('')
        }
        setIsValidating(false)
      }, 2000)

      return () => clearTimeout(timeout)
     } else if (documentType === 9) {
       // Assuming "9" is the ID for RUT
       setIsValidating(true)
       const timeoutRUT = setTimeout(() => {
         if (documentNumber && validateRUT(documentNumber)) {
           setCuitRUTError(translate('ra.message.error_RUT'))
         } else {
           setCuitRUTError('')
         }
         setIsValidating(false)
       }, 2000)

       return () => clearTimeout(timeoutRUT)
     } else {
       setCuitRUTError('')
       setIsValidating(false)
     }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentNumber, documentType])

  useEffect(() => {
    if (documentNumber && documentNumber !== record?.fiscal_data_relation?.document_number) {
      setLoadingExistDocumentNumber(true)
      const timeout = setTimeout(() => {
        checkDocumentNumber(documentNumber)
      }, 2000)
      return () => clearTimeout(timeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentNumber])

  const validateCUIT = (cuit) => {
    const cuitPattern = /^\d{2}-?\d{8}-?\d{1}$/
    const cleanedCUIT = cuit.replace(/-/g, '')
    return cuitPattern.test(cuit) && cleanedCUIT.length === 11
  }

  const validateRUT = (rut) => {
    if (rut){
      return rut?.length > 12
    }
  }

  const checkDocumentNumber = async (fiscal_data__document_number) => {
    try {
      const response = await businessUnitServices.business_unit_filter({
        fiscal_data__document_number,
        order_by: 'id',
      })
      if (response.length > 0) {
        setExistsError(translate('ra.message.number_exist')) 
      } else {
        setExistsError('')
      }
    } catch (error) {
      setExistsError('')
    } finally {
      setLoadingExistDocumentNumber(false)
    }
  }

  return (
    <EditInputsWrap label={translate('ra.field.document_number')} required>
      <TextInput
        source='fiscal_data_relation.document_number'
        label={documentType === 2 ? '27-00000000-6' : ''}
        sx={{
          '& legend': { display: 'none' },
          '& .MuiInputLabel-shrink': { opacity: 0, transition: 'all 0.2s ease-in' },
        }}
        fullWidth
        validate={[required()]}
      />
      {documentType === 2 && (
        <>
          {isValidating && cuitError ? (
            <CircularProgress size={20} />
          ) : (
            cuitError && <div style={{ color: 'red' }}>{cuitError}</div>
          )}
        </>
      )}
      {documentType === 9 && (
        <>
          {isValidating && RUTError ? (
            <CircularProgress size={20} />
          ) : (
            RUTError && <div style={{ color: 'red' }}>{RUTError}</div>
          )}
        </>
      )}
      {documentNumber !== record?.fiscal_data_relation?.document_number && (
        <>
          {loadingExistDocumentNumber ? (
            <CircularProgress size={20} />
          ) : (
            existsError && <div style={{ color: 'red' }}>{existsError}</div>
          )}
        </>
      )}
    </EditInputsWrap>
  )
}

export const FormFiscalData = ({ record, isSupplier=false }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const { identity } = useGetIdentity()
  // const [datafiscalLoading, setDatafiscalLoading] = useState(false)
  const [loadingAllApplicationByClient, setLoadingApplicationByClient] = useState(false)
  const [applicationByClient, setApplicationByClient] = useState({})
  const [optionsDocumentType, setOptionsDocumentType] = useState([])
  const [loadingDocumentType, setLoadingDocumentType] = useState(false)

  const [typeResponsible, setTypeResponsible] = useState([])
  const [loadingTypeResponsible, setLoadingTypeResponsible] = useState(false)

  // const [countryList, setCountryList] = useState([])
  // const [loadingCountryList, setLoadingCountryList] = useState(false)

  // const [searchByDocType, setSearchByDocType] = useState('')
  // const [searchByDocNumber, setSearchByDocNumber] = useState()

  const accountingTypes = [{id:3, name:'Prestadores Cia Aéreas'}, {id:5, name:'Prestadores internacionales'}, {id:4, name:'Prestadores nacionales'}]

  useEffect(() => {
    getTypeResponsible()
    // getCountry()
    getDocumentType()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (identity?.client_id){
      getApplicationByClient(identity?.client_id)
    }
  }, [identity?.client_id])

  // const handleChangeDocNumber = (event) => {
  //   setSearchByDocNumber(event.target.value)
  // }

  // const handleChangeDocType = (event) => {
  //   setSearchByDocType(event.target.value)
  // }


  const getApplicationByClient = async (client_id) => {
    try {
      setLoadingApplicationByClient(true)
      const applicationByClient = await clientServices.getApplicationByClient(client_id)
      if (applicationByClient && Array.isArray(applicationByClient)) {
        setApplicationByClient(applicationByClient?.[0])
        return
      }
    } catch (error) {
    } finally{
      setLoadingApplicationByClient(false)
    }
  }

  const getTypeResponsible = async () => {
    try {
      setLoadingTypeResponsible(true)
      const allTypeResponsible = await typeResponsibleService.getByAll()
      setTypeResponsible(allTypeResponsible)
      setLoadingTypeResponsible(false)
    } catch (error) {
      setTypeResponsible([])
      setLoadingTypeResponsible(false)
    }
  }

  // const searchFiscalData = async () => {
  //   setDatafiscalLoading(true)
  //   try {
  //     const fiscalDatarRgistries = await fiscalDataServices.getByAll(searchByDocNumber, searchByDocType)
  //     setValue('fiscal_data_relation', {
  //       name: fiscalDatarRgistries[0].name,
  //       tax_id: fiscalDatarRgistries[0].tax_id,
  //       address: fiscalDatarRgistries[0].address,
  //       region: fiscalDatarRgistries[0].region,
  //       document_type_id: fiscalDatarRgistries[0].document_type_id,
  //       document_number: fiscalDatarRgistries[0].document_number,
  //       type_responsible: fiscalDatarRgistries[0].type_responsible,
  //       is_agency: fiscalDatarRgistries[0].is_agency,
  //       country_id: fiscalDatarRgistries[0].country_id,
  //       tma_code: fiscalDatarRgistries[0].tma_code,
  //     })
  //     setValue('fiscal_data_id', fiscalDatarRgistries[0].id)
  //     notify(translate('ra.message.yes_search_found'), { type: 'success' })
  //     setDatafiscalLoading(false)
  //   } catch (error) {
  //     console.log('No se encontraron coincidencias: ', error)
  //     notify(translate('ra.message.no_search_found'), { type: 'warning' })
  //     setDatafiscalLoading(false)
  //   }
  // }

  // const getCountry = async () => {
  //   try {
  //     setLoadingCountryList(true)
  //     const allCountries = await countryServices.getByAll()
  //     if (allCountries) {
  //       setCountryList(allCountries)
  //       setLoadingCountryList(false)
  //       return
  //     }
  //     notify(`applications not found`, { type: 'error' })
  //     setLoadingCountryList(false)
  //   } catch (error) {
  //     console.log('ModuleCreate application getAll error: ', error)
  //     notify(`please, refresh page`, { type: 'error' })
  //     setLoadingCountryList(false)
  //   }
  // }

  const getDocumentType = async () => {
    try {
      setLoadingDocumentType(true)
      const allDocumentType = await documentTypeServices.getByAll()
      if (allDocumentType) {
        setOptionsDocumentType(allDocumentType)
        setLoadingDocumentType(false)
        return
      }
      notify(`applications not found`, { type: 'error' })
      setLoadingDocumentType(false)
    } catch (error) {
      notify(`please, refresh page`, { type: 'error' })
      setLoadingDocumentType(false)
    }
  }

  const isShowFantasyName = (formData) => {
    const typeFound = typeResponsible.find(
      (type) => formData?.fiscal_data_relation?.type_responsible_id === type?.id
    )
    if (typeFound) {
      const typeFoundFormat = typeFound?.name.replace(/\s+/g, '').toUpperCase()
      if (typeFoundFormat) {
        if (
          /* (MONOTRIBUTISTA === typeFoundFormat ||
          RESPONSABLE_MONOTRIBUTISTA === typeFoundFormat ||
          RESPONSABLE_INSCRIPTO === typeFoundFormat ||
          EMPRESA === typeFoundFormat) */
          (typeFoundFormat !== CONSUMIDOR_FINAL_NAME) &&
          !isSupplier
        ) {
          return true
        }
      }
    }
    return false
  }

  const isShowBusinessName = (formData) => {
    const typeFound = typeResponsible.find(
      (type) => formData?.fiscal_data_relation?.type_responsible_id === type?.id
    )
    if (typeFound) {
      const typeFoundFormat = typeFound?.name.replace(/\s+/g, '').toUpperCase()
      if (typeFoundFormat) {
        if (
          (CONSUMIDOR_FINAL_NAME === typeFoundFormat ||
          MONOTRIBUTISTA === typeFoundFormat ||
          RESPONSABLE_MONOTRIBUTISTA === typeFoundFormat) &&
          !isSupplier
        ) {
          return true
        }
      }
    }
    return false
  }

  const getPointOfSaleByConfigurationClient = (applicationByClient) => {
    try {
        const point_of_sale = applicationByClient?.config?.point_of_sale
        if (point_of_sale && Array.isArray(point_of_sale)){
          return point_of_sale
        }
        return []
    } catch (error) {
      return []
    }
  }

  const validateIfPointOfSaleIsRequired = (applicationByClient) => {
    try {
      const point_of_sale = applicationByClient?.config?.point_of_sale
      if (point_of_sale && Array.isArray(point_of_sale)){
        return true
      }
      return false
    } catch (error) {
      return false
    }
  }

  return (
    <Grid container spacing={3}>
      {/* <Grid item xs={12}>
        <Grid container spacing={3} className='formRelatedFields'>
          <Grid item xs={12}>
            <Typography variant='h5' component='div'>
              Autocompletar con:
            </Typography>
          </Grid>
          <EditInputsWrap label={translate('ra.field.document_type')}>
            <Select value={searchByDocType} onChange={handleChangeDocType} size='small' fullWidth>
              {optionsDocumentType.map((e, i) => (
                <MenuItem key={i} value={e.id}>
                  {e.short_name}
                </MenuItem>
              ))}
            </Select>
          </EditInputsWrap>
          <EditInputsWrap label={translate('ra.field.document_number')}>
            <TextField
              label=''
              value={searchByDocNumber}
              onChange={handleChangeDocNumber}
              size='small'
              fullWidth
            />
          </EditInputsWrap>
          <EditInputsWrap label=''>
            <Button
              onClick={async () => {
                await searchFiscalData()
              }}
              variant='outlined'
            >
              {translate('ra.action.search')}
            </Button>
          </EditInputsWrap>
        </Grid>
      </Grid>
      {datafiscalLoading ? (
        <>
          {[...Array(9)].map((e, i) => (
            <EditInputsWrap label={null}>
              <Skeleton variant='rectangular' width={'100%'} height={40} />
            </EditInputsWrap>
          ))}
        </>
      ) : (
        <>
          <EditInputsWrap label={translate('ra.field.name')}>
            <TextInput source='fiscal_data_relation.name' label='' fullWidth />
          </EditInputsWrap>
          <EditInputsWrap label={translate('ra.field.tax_id')}>
            <TextInput source='fiscal_data_relation.tax_id' label='' fullWidth />
          </EditInputsWrap>
          <EditInputsWrap label={translate('ra.field.address')}>
            <TextInput source='fiscal_data_relation.address' label='' fullWidth  />
          </EditInputsWrap>
          <EditInputsWrap label={translate('ra.field.region')}>
            <TextInput source='fiscal_data_relation.region' label='' fullWidth  />
          </EditInputsWrap>
          <EditInputsWrap label={translate('ra.field.country')}>
            {loadingCountryList ? (
              <Skeleton variant='rectangular' width={'100%'} height={40} />
            ) : (
              <SelectInput
                optionText='long_name'
                optionValue='id'
                source='fiscal_data_relation.country_id'
                choices={countryList}
                fullWidth
                label=''
                shouldRenderSuggestions={(val) => { return val.trim().length > 1 }}
                helperText={translate('ra.validation.minCharacters')}
              />
            )}
          </EditInputsWrap>
        </>
      )} */}
      <EditInputsWrap label={translate('ra.field.document_type')} required>
        {loadingDocumentType ? (
          <Skeleton variant='rectangular' width={'100%'} height={40} />
        ) : (
          <SelectInput
            source='fiscal_data_relation.document_type_id'
            optionText='short_name'
            optionValue='id'
            fullWidth
            choices={optionsDocumentType}
            label=''
            validate={[required()]}
          />
        )}
      </EditInputsWrap>
      <DocumentNumberInput record={record} />
      <EditInputsWrap label={translate('ra.field.type_responsible')} required>
        {loadingTypeResponsible ? (
          <Skeleton variant='rectangular' width={'100%'} height={40} />
        ) : (
          <SelectInput
            source='fiscal_data_relation.type_responsible_id'
            optionText='name'
            optionValue='id'
            fullWidth
            choices={typeResponsible}
            label=''
            validate={[required()]}
          />
        )}
      </EditInputsWrap>
      <FormDataConsumer>
        {({ formData }) => {
          const typeFound = typeResponsible.find(
            (type) => formData?.fiscal_data_relation?.type_responsible_id === type?.id
          )
          if (typeFound) {
            const typeFoundFormat = typeFound?.name.replace(/\s+/g, '').toUpperCase()
            if (typeFoundFormat) {
              if (
                (CONSUMIDOR_FINAL_NAME === typeFoundFormat ||
                MONOTRIBUTISTA === typeFoundFormat ||
                RESPONSABLE_MONOTRIBUTISTA === typeFoundFormat) &&
                !isSupplier
              ) {
                return (
                  <Grid container>
                    <EditInputsWrap label={translate('ra.field.name')} required>
                      <TextInput source='short_name' label='' fullWidth validate={[required()]} />
                    </EditInputsWrap>
                    <EditInputsWrap label={translate('ra.field.surname')} required>
                      <TextInput source='long_name' label='' fullWidth validate={[required()]} />
                    </EditInputsWrap>
                  </Grid>
                )
              }
            }
          }
        }}
      </FormDataConsumer>
      <FormDataConsumer>
        {({ formData }) => {
          if (isShowBusinessName(formData)) {
            return <BusinessName />
          }
          return (
            <EditInputsWrap label={translate('ra.field.business_name')}>
              <TextInput source='fiscal_data_relation.name' label='' fullWidth />
            </EditInputsWrap>
          )
        }}
      </FormDataConsumer>
      <FormDataConsumer>
        {({ formData }) =>
          isShowFantasyName(formData) && (
            <EditInputsWrap label={translate('ra.field.fantasy_name')}>
              <TextInput source='fantasy_name' label='' fullWidth />
            </EditInputsWrap>
          )
        }
      </FormDataConsumer>
      <EditInputsWrap label={translate('ra.field.tma_code')}>
        <TextInput source='fiscal_data_relation.tma_code' label='' fullWidth />
      </EditInputsWrap>
      {!isSupplier?<EditInputsWrap label={translate('ra.field.point_of_sale')} required={validateIfPointOfSaleIsRequired(applicationByClient)}>
        {loadingAllApplicationByClient ? (
            <Skeleton variant='rectangular' width={'100%'} height={40} />
          ) : (
          <SelectInput
            source='fiscal_data_relation.point_of_sale'
            label=''
            optionValue='id'
            optionText='name'
            choices={getPointOfSaleByConfigurationClient(applicationByClient)}
            fullWidth
            validate={validateIfPointOfSaleIsRequired(applicationByClient) && [required()]}
          />
        )}
      </EditInputsWrap>:''}
      {isSupplier?<EditInputsWrap label={translate('ra.field.accounting_config_code')} >
        {loadingAllApplicationByClient ? (
            <Skeleton variant='rectangular' width={'100%'} height={40} />
          ) : (
          <SelectInput
            source='fiscal_data_relation.accounting_config_code'
            label=''
            optionValue='id'
            optionText='name'
            choices={accountingTypes}
            fullWidth
            validate={[required()]}
          />
        )}
      </EditInputsWrap>:''}
    </Grid>
  )
}
